import * as m from 'motion/react-m'
import { useScroll, useMotionValue, useSpring, useTransform, AnimatePresence } from 'motion/react'
import { Motion } from '@/lib/motion/lazy-motion'
import { useCallback, useEffect, memo } from 'react'
import Image from 'next/image'
import { useDeferredActivation } from '@/hooks/useDeferredActivation'

const BaseLayers = memo(function BaseLayers() {
  function ScreenLayer({
    index,
    zIndex,
    translateY = 0,
    translateX = 0,
  }: {
    index: number
    zIndex: number | string
    translateY?: number
    translateX?: number
  }) {
    return (
      <div
        key={`screen-${index}`}
        className="absolute -left-3.5 top-9 h-full w-full rounded-2xl border border-white border-opacity-10 bg-gray-800 shadow-[0px_50px_100px_-25px_rgba(0,0,0,1)] md:-left-6"
        style={{
          zIndex,
          opacity: index * 0.2,
          transform: `translateY(${translateY}px) translateX(${translateX}px) skewX(18deg) skewY(0deg) rotate(-5deg)`,
        }}
      />
    )
  }

  return (
    <>
      {[4, 3, 2, 1].map((index) => (
        <ScreenLayer
          key={index}
          index={index}
          zIndex={index}
          translateY={(4 - index) * 30}
          translateX={(4 - index) * -10}
        />
      ))}
    </>
  )
})

function ScreenshotLayer({
  screenshot,
  interactive,
}: {
  screenshot?: string
  interactive: boolean
}) {
  return (
    <m.div
      initial={
        interactive
          ? {
              opacity: 0,
              scale: 0.7,
              y: 30,
              rotate: '-12deg',
            }
          : false
      }
      animate={{
        opacity: 1,
        scale: 1,
        y: 0,
        rotate: '-5deg',
      }}
      exit={{
        opacity: 0,
        scale: 0.7,
        y: -30,
        rotate: '0deg',
      }}
      transition={{
        duration: 0.3,
        ease: [0.19, 1.0, 0.22, 1.0],
      }}
      className="absolute left-0 h-full w-full rounded-2xl bg-cover bg-no-repeat shadow-[0px_50px_100px_-25px_rgba(0,0,0,1)] after:absolute after:inset-0 after:rounded-2xl after:border after:border-white after:border-opacity-10"
      style={{
        zIndex: 10,
        skewX: '18deg',
      }}
    >
      {screenshot && (
        <Image
          src={screenshot}
          alt=""
          fill
          priority
          sizes="(min-width: 1536px) 1080px, (min-width: 1024px) 828px, 90vw"
          className="rounded-2xl object-cover object-left-top"
        />
      )}
    </m.div>
  )
}

export function Visual({ screenshot }: { screenshot?: string }) {
  const { ref, active: interactive } = useDeferredActivation({
    strategy: 'after-interaction-idle',
  })
  const height = 680
  const { scrollY } = useScroll()
  const springConfig = { damping: 20, stiffness: 333 }

  const screenX = useMotionValue(0)
  const screenY = useMotionValue(0)
  const screenSpringX = useSpring(screenX, springConfig)
  const screenSpringY = useSpring(screenY, springConfig)

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const { innerWidth, innerHeight } = window
      const { clientX, clientY } = e

      const relativeX = clientX - innerWidth / 2
      const relativeY = clientY - innerHeight / 2

      screenX.set(relativeX / 60)
      screenY.set(relativeY / 60)
    },
    [screenX, screenY],
  )

  useEffect(() => {
    if (!interactive) return
    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [interactive, handleMouseMove])

  const scrollScreen = useTransform(scrollY, [0, height], [0, -150])
  const opacity = useTransform(scrollY, [0, height], [1, 0])

  return (
    <Motion>
      <m.div
        ref={ref}
        initial={false}
        style={interactive ? { opacity } : undefined}
        className="absolute -bottom-[12%] left-1/2 ml-[50px] aspect-[155/100] max-h-[50%] w-[90%] -translate-x-1/2 xs:-bottom-[15%] xs:w-[75%] sm:-bottom-[20%] md:-bottom-[30%] md:w-[70vw] lg:ml-[80px] lg:max-h-[50%] lg:w-[70vw] xl:-bottom-[50%] xl:max-h-[70%] 2xl:ml-[100px] 2xl:w-[1080px]"
      >
        <div className="relative h-full w-full scale-[1]">
          <m.div
            className="absolute left-0 top-0 z-20 h-full w-full"
            style={
              interactive
                ? {
                    y: scrollScreen,
                    x: screenSpringX,
                    translateY: screenSpringY,
                  }
                : undefined
            }
          >
            <BaseLayers />

            <AnimatePresence mode="wait">
              {screenshot && (
                <ScreenshotLayer
                  key={screenshot}
                  screenshot={screenshot}
                  interactive={interactive}
                />
              )}
            </AnimatePresence>
          </m.div>
        </div>
      </m.div>
    </Motion>
  )
}
